import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { activityRunService } from '~/services/runAPI';
import { artifactService } from '~/services/taskDataAPI';
import { Artifact } from '~/types/resources';
import { apiInstance } from '~/utils/api/client';
import {
  formatErrorPayload,
  QueryError,
  QueryKey,
  UseApiMutationOptions,
  UseApiQueryOptions,
  useCurrentWorkspaceQueryKey,
  validateApiResponse,
} from '../utils';

const ROOT_KEY = 'artifact';
const ROOT_LINK_KEY = 'artifact_link';
const getQueryKey = (stepRunId: string) => [ROOT_KEY, stepRunId];
const getLinksQueryKey = (stepRunId: string) => [ROOT_LINK_KEY, stepRunId];
const getLinkQueryKey = (stepRunId: string, artifactId: string, contentDisposition?: string) => [
  ...getLinksQueryKey(stepRunId),
  artifactId,
  contentDisposition,
];

export enum ArtifactQueryErrors {
  GET_ACTIVITY_RUN_ARTIFACTS_ERROR = 'GET_ACTIVITY_RUN_ARTIFACTS_ERROR',
  GET_ACTIVITY_RUN_ARTIFACT_LINK_ERROR = 'GET_ACTIVITY_RUN_ARTIFACT_LINK_ERROR',
}

export const useArtifactsQuery = (
  {
    processId,
    processRunId,
    activityRunId,
  }: { processId: string; processRunId: string; activityRunId: string },
  options?: UseQueryOptions<Artifact[] | undefined, QueryError, Artifact[], QueryKey>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getQueryKey(activityRunId),
    async () => {
      const artifactUrl = validateApiResponse(
        await activityRunService.getActivityRunArtifactsLink({
          workspaceId,
          processId,
          processRunId,
          activityRunId,
        }),
      ).data;

      const artifactsLink = apiInstance({ url: artifactUrl as string });
      return validateApiResponse(await artifactsLink.get<{ data: Artifact[] }>('')).data.data;
    },
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: ArtifactQueryErrors.GET_ACTIVITY_RUN_ARTIFACTS_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useArtifactLinkQuery = (
  {
    processId,
    processRunId,
    activityRunId,
    artifactId,
    artifactName,
    contentDisposition,
  }: {
    processId: string;
    processRunId: string;
    activityRunId: string;
    artifactId: string;
    artifactName: string;
    contentDisposition?: 'inline' | 'attachment';
  },
  options?: UseApiQueryOptions<typeof activityRunService.getActivityRunArtifactLink>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getLinkQueryKey(activityRunId, artifactId, contentDisposition),
    async () =>
      validateApiResponse(
        await activityRunService.getActivityRunArtifactLink({
          workspaceId,
          processId,
          processRunId,
          activityRunId,
          artifactId,
          artifactName,
          contentDisposition,
        }),
      ).data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: ArtifactQueryErrors.GET_ACTIVITY_RUN_ARTIFACT_LINK_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useDeleteArtifactsMutation = (
  options?: UseApiMutationOptions<typeof artifactService.deleteArtifacts>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ stepRunId }: { stepRunId: string }) =>
      validateApiResponse(await artifactService.deleteArtifacts({ workspaceId, stepRunId })).data,
    {
      onSuccess: (_, { stepRunId }) => {
        queryClient.invalidateQueries(getQueryKey(stepRunId));
        queryClient.invalidateQueries(getLinksQueryKey(stepRunId));
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDeleteArtifactMutation = (
  options?: UseApiMutationOptions<typeof artifactService.deleteArtifact>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ stepRunId, artifactId }: { stepRunId: string; artifactId: string }) =>
      validateApiResponse(
        await artifactService.deleteArtifact({ workspaceId, stepRunId, artifactId }),
      ).data,
    {
      onSuccess: (_, { stepRunId, artifactId }) => {
        queryClient.invalidateQueries(getQueryKey(stepRunId));
        queryClient.invalidateQueries(getLinkQueryKey(stepRunId, artifactId));
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
